import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DragHandleOutlined,
  Sort,
} from "@mui/icons-material";
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $nodesOfType,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
  LexicalNode,
} from "lexical";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useOptionalAutosaveContext } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/AutoSavePlugin";
import { useEditorScrollSubscription } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/editorScrollSubscription";
import "./TableHoverActionsPlugin.css";

// Named constants for timing values
const MUTATION_DELAY_MS = 10;
const DEBOUNCE_MS = 50;
const HIDE_DELAY_MS = 50;
const EXTENSION_PADDING = 16;
const SORT_ICON_INSET_PX = 12;

const closestFromEventTarget = (
  target: EventTarget | null,
  selector: string
): Element | null => {
  if (!(target instanceof Element)) return null;
  return target.closest(selector);
};

interface ButtonPosition {
  top: number;
  left: number;
  index: number;
}

interface HoverExtensionArea {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface DropZone {
  top: number;
  left: number;
  width: number;
  height: number;
  index: number;
  // Position for the drop indicator line (at the border)
  indicatorPosition: number;
}

interface TablePositions {
  tableElement: HTMLTableElement;
  tableNodeKey: string;
  columnPositions: ButtonPosition[];
  columnSortPositions: ButtonPosition[];
  rowPositions: ButtonPosition[];
  columnReorderPositions: ButtonPosition[];
  rowReorderPositions: ButtonPosition[];
  rowDropZones: DropZone[];
  columnDropZones: DropZone[];
  hoverExtensions: {
    top: HoverExtensionArea;
    left: HoverExtensionArea;
  };
}

interface DragInfo {
  type: "row" | "column";
  sourceIndex: number;
  currentTargetIndex: number | null;
}

interface TableHoverZone {
  tableElement: HTMLTableElement;
  // Zone above the table (for column buttons)
  topZone: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  // Zone to the left of the table (for row buttons)
  leftZone: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

// Consolidated state type
interface HoverActionsState {
  tablePositions: TablePositions | null;
  isHovering: boolean;
  tableHoverZones: TableHoverZone[];
  dragInfo: DragInfo | null;
  hoveredRowIndex: number | null;
  hoveredColumnIndex: number | null;
}

type SortDirection = "asc" | "desc";

type SortTableColumnPayload = {
  columnIndex: number;
  direction?: SortDirection;
};

export const SORT_TABLE_COLUMN_COMMAND: LexicalCommand<SortTableColumnPayload> =
  createCommand("SORT_TABLE_COLUMN_COMMAND");

type TableSortState = {
  columnIndex: number;
  direction: SortDirection;
};

type SortStateByTable = Record<string, TableSortState>;

// Action types
type HoverActionsAction =
  | { type: "SET_TABLE_POSITIONS"; payload: TablePositions | null }
  | {
      type: "UPDATE_POSITIONS";
      payload: Omit<TablePositions, "tableElement" | "tableNodeKey">;
    }
  | { type: "SET_HOVERING"; payload: boolean }
  | { type: "SET_HOVER_ZONES"; payload: TableHoverZone[] }
  | {
      type: "START_DRAG";
      payload: { dragType: "row" | "column"; sourceIndex: number };
    }
  | { type: "UPDATE_DRAG_TARGET"; payload: number }
  | { type: "END_DRAG" }
  | {
      type: "SET_HOVERED_CELL";
      payload: { rowIndex: number | null; columnIndex: number | null };
    }
  | { type: "HIDE_CONTROLS" }
  | { type: "SHOW_CONTROLS"; payload: TablePositions };

const initialState: HoverActionsState = {
  tablePositions: null,
  isHovering: false,
  tableHoverZones: [],
  dragInfo: null,
  hoveredRowIndex: null,
  hoveredColumnIndex: null,
};

function hoverActionsReducer(
  state: HoverActionsState,
  action: HoverActionsAction
): HoverActionsState {
  switch (action.type) {
    case "SET_TABLE_POSITIONS":
      return { ...state, tablePositions: action.payload };
    case "UPDATE_POSITIONS":
      if (!state.tablePositions) return state;
      return {
        ...state,
        tablePositions: {
          ...state.tablePositions,
          ...action.payload,
        },
      };
    case "SET_HOVERING":
      return { ...state, isHovering: action.payload };
    case "SET_HOVER_ZONES":
      return { ...state, tableHoverZones: action.payload };
    case "START_DRAG":
      return {
        ...state,
        dragInfo: {
          type: action.payload.dragType,
          sourceIndex: action.payload.sourceIndex,
          currentTargetIndex: null,
        },
      };
    case "UPDATE_DRAG_TARGET":
      if (
        !state.dragInfo ||
        state.dragInfo.currentTargetIndex === action.payload
      ) {
        return state;
      }
      return {
        ...state,
        dragInfo: { ...state.dragInfo, currentTargetIndex: action.payload },
      };
    case "END_DRAG":
      return { ...state, dragInfo: null };
    case "SET_HOVERED_CELL":
      return {
        ...state,
        hoveredRowIndex: action.payload.rowIndex,
        hoveredColumnIndex: action.payload.columnIndex,
      };
    case "HIDE_CONTROLS":
      return {
        ...state,
        isHovering: false,
        tablePositions: null,
        hoveredRowIndex: null,
        hoveredColumnIndex: null,
      };
    case "SHOW_CONTROLS":
      return {
        ...state,
        tablePositions: action.payload,
        isHovering: true,
      };
    default:
      return state;
  }
}

function TableHoverActionsPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const { triggerAutosave } = useOptionalAutosaveContext();
  const [state, dispatch] = useReducer(hoverActionsReducer, initialState);
  const [sortState, setSortState] = useState<SortStateByTable>({});

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mutationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tableKeyRef = useRef<string | null>(null);

  // Use ref to access latest state in callbacks without re-creating them
  const stateRef = useRef(state);
  stateRef.current = state;
  const sortStateRef = useRef(sortState);
  sortStateRef.current = sortState;

  // Track all tables and their hover zones
  const updateTableHoverZones = useCallback(() => {
    const editorRoot =
      editor.getRootElement() ??
      (document.querySelector(".editor-input") as HTMLElement | null);
    if (!editorRoot) return;

    const tables = editorRoot.querySelectorAll("table");
    const zones: TableHoverZone[] = [];

    tables.forEach((table) => {
      const tableElement = table as HTMLTableElement;
      const rect = tableElement.getBoundingClientRect();
      zones.push({
        tableElement,
        // Zone above the table (doesn't overlap with table)
        topZone: {
          top: rect.top - EXTENSION_PADDING,
          left: rect.left - EXTENSION_PADDING,
          width: rect.width + EXTENSION_PADDING * 2,
          height: EXTENSION_PADDING,
        },
        // Zone to the left of the table (doesn't overlap with table)
        leftZone: {
          top: rect.top,
          left: rect.left - EXTENSION_PADDING,
          width: EXTENSION_PADDING,
          height: rect.height,
        },
      });
    });

    dispatch({ type: "SET_HOVER_ZONES", payload: zones });
  }, [editor]);

  // Update hover zones on editor scroll via shared coordinator.
  const handleEditorScroll = useCallback(() => {
    updateTableHoverZones();
  }, [updateTableHoverZones]);
  useEditorScrollSubscription(editor, handleEditorScroll);

  // Update hover zones on viewport resize.
  useEffect(() => {
    const handleResize = () => {
      updateTableHoverZones();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateTableHoverZones]);

  // Update hover zones when tables change
  useEffect(() => {
    const unregister = editor.registerMutationListener(TableNode, () => {
      // Small delay to let DOM update
      mutationTimeoutRef.current = setTimeout(
        updateTableHoverZones,
        MUTATION_DELAY_MS
      );
    });

    return () => {
      unregister();
      if (mutationTimeoutRef.current) {
        clearTimeout(mutationTimeoutRef.current);
        mutationTimeoutRef.current = null;
      }
    };
  }, [editor, updateTableHoverZones]);

  // Ensure hover zones are created when tables appear after initial render
  useEffect(() => {
    return editor.registerUpdateListener(() => {
      const editorRoot =
        editor.getRootElement() ??
        (document.querySelector(".editor-input") as HTMLElement | null);
      if (!editorRoot) return;
      const tableCount = editorRoot.querySelectorAll("table").length;
      const currentCount = stateRef.current.tableHoverZones.length;
      if (tableCount !== currentCount) {
        updateTableHoverZones();
      }
    });
  }, [editor, updateTableHoverZones]);

  const calculateButtonPositions = useCallback(
    (
      tableElement: HTMLTableElement
    ): Omit<TablePositions, "tableElement" | "tableNodeKey"> => {
      const tableRect = tableElement.getBoundingClientRect();
      const columns: ButtonPosition[] = [];
      const columnSort: ButtonPosition[] = [];
      const rows: ButtonPosition[] = [];
      const columnReorder: ButtonPosition[] = [];
      const rowReorder: ButtonPosition[] = [];
      const rowDropZones: DropZone[] = [];
      const columnDropZones: DropZone[] = [];

      // Get all header cells (first row) for column positions
      // Position: 13px above table (3px gap + 10px half of 20px expanded button)
      const headerRow = tableElement.querySelector("tr");
      if (headerRow) {
        const cells = Array.from(headerRow.querySelectorAll("th, td"));
        cells.forEach((cell, index) => {
          const cellRect = cell.getBoundingClientRect();
          // Add column button at right edge of cell
          columns.push({
            top: tableRect.top - 13,
            left: cellRect.right,
            index,
          });
          columnSort.push({
            top: cellRect.top + cellRect.height / 2,
            left: cellRect.right - SORT_ICON_INSET_PX,
            index,
          });
          // Reorder button centered horizontally on cell
          columnReorder.push({
            top: tableRect.top - 13,
            left: cellRect.left + cellRect.width / 2,
            index,
          });
          // Column drop zone - spans the full height of the table
          // Each zone is positioned over the column, and the indicator will show at the right edge
          columnDropZones.push({
            top: tableRect.top,
            left: cellRect.left,
            width: cellRect.width,
            height: tableRect.height,
            index,
            indicatorPosition: cellRect.right, // Right edge of column
          });
        });
      }

      // Get all rows for row positions
      // Position: 13px left of table (3px gap + 10px half of 20px expanded button)
      const allRows = Array.from(tableElement.querySelectorAll("tr"));
      allRows.forEach((row, index) => {
        const rowRect = row.getBoundingClientRect();
        // Add row button at bottom edge of row
        rows.push({
          top: rowRect.bottom,
          left: tableRect.left - 13,
          index,
        });
        // Reorder button centered vertically on row
        rowReorder.push({
          top: rowRect.top + rowRect.height / 2,
          left: tableRect.left - 13,
          index,
        });
        // Row drop zone - spans the full width of the table
        // Each zone is positioned over the row, and the indicator will show at the bottom edge
        rowDropZones.push({
          top: rowRect.top,
          left: tableRect.left,
          width: tableRect.width,
          height: rowRect.height,
          index,
          indicatorPosition: rowRect.bottom, // Bottom edge of row
        });
      });

      // Calculate hover extension areas
      const hoverExtensions = {
        // Top extension (for column buttons) - extends above the table
        top: {
          top: tableRect.top - EXTENSION_PADDING,
          left: tableRect.left - EXTENSION_PADDING,
          width: tableRect.width + EXTENSION_PADDING * 2,
          height: EXTENSION_PADDING,
        },
        // Left extension (for row buttons) - extends to the left of the table
        left: {
          top: tableRect.top,
          left: tableRect.left - EXTENSION_PADDING,
          width: EXTENSION_PADDING,
          height: tableRect.height + EXTENSION_PADDING,
        },
      };

      return {
        columnPositions: columns,
        columnSortPositions: columnSort,
        rowPositions: rows,
        columnReorderPositions: columnReorder,
        rowReorderPositions: rowReorder,
        rowDropZones,
        columnDropZones,
        hoverExtensions,
      };
    },
    []
  );

  const showTableControls = useCallback(
    (tableElement: HTMLTableElement) => {
      if (!editor.isEditable()) {
        return;
      }

      // Cancel any pending hide
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Debounce the hover detection
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        // Find a cell in the table to get the TableNode
        const firstCell = tableElement.querySelector("th, td");
        if (!firstCell) return;

        let tableNodeKey: string | null = null;
        editor.read(() => {
          const cellNode = $getNearestNodeFromDOMNode(firstCell);
          if (!cellNode) return;

          // Walk up to find the table node
          let current: LexicalNode | null = cellNode;
          let tableNode: TableNode | null = null;

          while (current) {
            if ($isTableNode(current)) {
              tableNode = current;
              break;
            }
            current = current.getParent();
          }

          if (tableNode) {
            tableNodeKey = tableNode.getKey();
          }
        });

        if (!tableNodeKey) return;

        const liveTableElement = editor.getElementByKey(tableNodeKey);
        const resolvedTableElement =
          liveTableElement instanceof HTMLTableElement
            ? liveTableElement
            : tableElement;
        const positions = calculateButtonPositions(resolvedTableElement);
        dispatch({
          type: "SHOW_CONTROLS",
          payload: {
            tableElement: resolvedTableElement,
            tableNodeKey,
            ...positions,
          },
        });
      }, DEBOUNCE_MS);
    },
    [editor, calculateButtonPositions]
  );

  const hideTableControls = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Small delay before hiding to prevent flickering when transitioning between elements
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      dispatch({ type: "HIDE_CONTROLS" });
    }, HIDE_DELAY_MS);
  }, []);

  const handleAddColumn = useCallback(
    (columnIndex: number) => {
      const currentState = stateRef.current;
      if (!currentState.tablePositions) return;

      editor.update(() => {
        const tableNode = $getNodeByKey(
          currentState.tablePositions!.tableNodeKey
        );
        if (!$isTableNode(tableNode)) return;

        const rows = tableNode.getChildren<TableRowNode>();
        if (rows.length === 0) return;

        const firstRow = rows[0];
        if (!$isTableRowNode(firstRow)) return;

        const cells = firstRow.getChildren<TableCellNode>();
        if (columnIndex >= cells.length) return;

        const targetCell = cells[columnIndex];
        if (!$isTableCellNode(targetCell)) return;

        // Select the cell to set context for insertion
        targetCell.selectEnd();

        // Insert column to the right (true = insertAfter)
        try {
          $insertTableColumn__EXPERIMENTAL(true);
        } catch (error) {
          console.error("Failed to insert table column:", error);
        }
      });

      // Trigger autosave after table modification
      triggerAutosave();
    },
    [editor, triggerAutosave]
  );

  const handleAddRow = useCallback(
    (rowIndex: number) => {
      const currentState = stateRef.current;
      if (!currentState.tablePositions) return;

      editor.update(() => {
        const tableNode = $getNodeByKey(
          currentState.tablePositions!.tableNodeKey
        );
        if (!$isTableNode(tableNode)) return;

        const rows = tableNode.getChildren<TableRowNode>();
        if (rowIndex >= rows.length) return;

        const targetRow = rows[rowIndex];
        if (!$isTableRowNode(targetRow)) return;

        const cells = targetRow.getChildren<TableCellNode>();
        if (cells.length === 0) return;

        const targetCell = cells[0];
        if (!$isTableCellNode(targetCell)) return;

        // Select the cell to set context for insertion
        targetCell.selectEnd();

        // Insert row below (true = insertAfter)
        try {
          $insertTableRow__EXPERIMENTAL(true);
        } catch (error) {
          console.error("Failed to insert table row:", error);
        }
      });

      // Trigger autosave after table modification
      triggerAutosave();
    },
    [editor, triggerAutosave]
  );

  // Drag handlers for reordering
  const handleDragStart = useCallback(
    (dragType: "row" | "column", sourceIndex: number) => {
      dispatch({ type: "START_DRAG", payload: { dragType, sourceIndex } });
    },
    []
  );

  const handleDragOver = useCallback((targetIndex: number) => {
    dispatch({ type: "UPDATE_DRAG_TARGET", payload: targetIndex });
  }, []);

  const handleDragEnd = useCallback(() => {
    dispatch({ type: "END_DRAG" });
  }, []);

  // Move row in Lexical
  const handleMoveRow = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      const currentState = stateRef.current;
      if (!currentState.tablePositions || sourceIndex === targetIndex) return;

      editor.update(() => {
        const tableNode = $getNodeByKey(
          currentState.tablePositions!.tableNodeKey
        );
        if (!$isTableNode(tableNode)) return;

        const rows = tableNode.getChildren<TableRowNode>();
        if (sourceIndex >= rows.length || targetIndex >= rows.length) return;
        if (sourceIndex < 0 || targetIndex < 0) return;

        const sourceRow = rows[sourceIndex];
        const targetRow = rows[targetIndex];

        if (!$isTableRowNode(sourceRow) || !$isTableRowNode(targetRow)) return;

        // Always insert after target since the drop indicator shows at the
        // bottom edge of the target row, indicating "insert after this row"
        targetRow.insertAfter(sourceRow);
      });

      // Trigger autosave after reordering
      triggerAutosave();
    },
    [editor, triggerAutosave]
  );

  // Move column in Lexical
  const handleMoveColumn = useCallback(
    (sourceIndex: number, targetIndex: number) => {
      const currentState = stateRef.current;
      if (!currentState.tablePositions || sourceIndex === targetIndex) return;

      editor.update(() => {
        const tableNode = $getNodeByKey(
          currentState.tablePositions!.tableNodeKey
        );
        if (!$isTableNode(tableNode)) return;

        const rows = tableNode.getChildren<TableRowNode>();
        if (rows.length === 0) return;

        // Validate column indices using the first row
        const firstRow = rows[0];
        if (!$isTableRowNode(firstRow)) return;
        const numColumns = firstRow.getChildrenSize();
        if (sourceIndex >= numColumns || targetIndex >= numColumns) return;
        if (sourceIndex < 0 || targetIndex < 0) return;

        // Move cells in each row
        rows.forEach((row) => {
          if (!$isTableRowNode(row)) return;
          const cells = row.getChildren<TableCellNode>();
          if (sourceIndex >= cells.length || targetIndex >= cells.length)
            return;

          const sourceCell = cells[sourceIndex];
          const targetCell = cells[targetIndex];

          if (!$isTableCellNode(sourceCell) || !$isTableCellNode(targetCell))
            return;

          // Always insert after target since the drop indicator shows at the
          // right edge of the target column, indicating "insert after this column"
          targetCell.insertAfter(sourceCell);
        });

        // Update colWidths if they exist
        const colWidths = tableNode.getColWidths();
        if (colWidths && colWidths.length > 0) {
          const newWidths = [...colWidths];
          const [movedWidth] = newWidths.splice(sourceIndex, 1);
          // Calculate the insertion position for the widths array
          // When moving right (source < target): target shifted by 1 after removal, so insert at targetIndex
          // When moving left (source > target): target didn't shift, so insert at targetIndex + 1
          const insertPosition =
            sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
          newWidths.splice(insertPosition, 0, movedWidth);
          tableNode.setColWidths(newWidths);
        }
      });

      // Trigger autosave after reordering
      triggerAutosave();
    },
    [editor, triggerAutosave]
  );

  const handleSortColumn = useCallback(
    (columnIndex: number, directionOverride?: SortDirection) => {
      const currentState = stateRef.current;
      let tableNodeKey =
        currentState.tablePositions?.tableNodeKey ?? tableKeyRef.current;
      let nextDirection: SortDirection | null = null;
      let didSort = false;

      editor.update(() => {
        let tableNode: TableNode | null = null;
        if (tableNodeKey) {
          const maybeTable = $getNodeByKey(tableNodeKey);
          if ($isTableNode(maybeTable)) {
            tableNode = maybeTable;
          }
        }
        if (!tableNode) {
          const tables = $nodesOfType(TableNode);
          tableNode = tables[0] ?? null;
          if (tableNode) {
            tableNodeKey = tableNode.getKey();
            tableKeyRef.current = tableNodeKey;
          }
        } else {
          tableKeyRef.current = tableNodeKey;
        }
        if (!tableNode || !tableNodeKey) return;

        const currentSort = sortStateRef.current[tableNodeKey];
        nextDirection =
          directionOverride ??
          (currentSort &&
          currentSort.columnIndex === columnIndex &&
          currentSort.direction === "asc"
            ? "desc"
            : "asc");

        const rows = tableNode.getChildren<TableRowNode>();
        if (rows.length === 0) return;

        const headerRows: TableRowNode[] = [];
        const dataRows: TableRowNode[] = [];

        rows.forEach((row) => {
          if (!$isTableRowNode(row)) return;
          const isHeaderRow = row.getChildren().some((cell) => {
            if (!$isTableCellNode(cell)) return false;
            return cell.__headerState === TableCellHeaderStates.ROW;
          });
          if (isHeaderRow) {
            headerRows.push(row);
          } else {
            dataRows.push(row);
          }
        });

        if (dataRows.length <= 1) return;

        const collator = new Intl.Collator(undefined, {
          numeric: true,
          sensitivity: "base",
        });

        const sortedRows = dataRows
          .map((row, index) => {
            const cells = row.getChildren<TableCellNode>();
            const cell = cells[columnIndex];
            const cellValue = $isTableCellNode(cell)
              ? cell.getTextContent().trim()
              : "";
            return { row, index, value: cellValue };
          })
          .sort((a, b) => {
            const aValue = a.value;
            const bValue = b.value;
            const aEmpty = aValue.length === 0;
            const bEmpty = bValue.length === 0;
            if (aEmpty && bEmpty) return a.index - b.index;
            if (aEmpty) return 1;
            if (bEmpty) return -1;

            const comparison = collator.compare(aValue, bValue);
            if (comparison === 0) return a.index - b.index;
            return nextDirection === "asc" ? comparison : -comparison;
          })
          .map((item) => item.row);

        const newOrder = [...headerRows, ...sortedRows];
        newOrder.forEach((row) => row.remove());
        newOrder.forEach((row) => tableNode.append(row));
        didSort = true;
      });

      if (didSort && tableNodeKey && nextDirection) {
        const resolvedKey = tableNodeKey;
        const resolvedDirection: SortDirection = nextDirection;
        setSortState((prev) => {
          const nextState = {
            ...prev,
            [resolvedKey]: {
              columnIndex,
              direction: resolvedDirection,
            },
          };
          sortStateRef.current = nextState;
          return nextState;
        });
        triggerAutosave();
      }
    },
    [editor, triggerAutosave]
  );

  useEffect(() => {
    return editor.registerCommand(
      SORT_TABLE_COLUMN_COMMAND,
      (payload) => {
        handleSortColumn(payload.columnIndex, payload.direction);
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, handleSortColumn]);

  // Handle drop - perform the actual reorder
  const handleDrop = useCallback(
    (targetIndex: number) => {
      const currentState = stateRef.current;
      if (!currentState.dragInfo) return;

      const { type, sourceIndex } = currentState.dragInfo;

      if (type === "row") {
        handleMoveRow(sourceIndex, targetIndex);
      } else {
        handleMoveColumn(sourceIndex, targetIndex);
      }

      dispatch({ type: "END_DRAG" });
    },
    [handleMoveRow, handleMoveColumn]
  );

  // Determine which row/column is hovered based on mouse position
  const updateHoveredCell = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const cell = target.closest("th, td") as HTMLTableCellElement | null;

    if (cell) {
      const row = cell.closest("tr") as HTMLTableRowElement | null;
      if (row) {
        const table = row.closest("table") as HTMLTableElement | null;
        if (table) {
          // Find the row index
          const rows = Array.from(table.querySelectorAll("tr"));
          const rowIndex = rows.indexOf(row);

          // Find the column index (account for colspan)
          let columnIndex = 0;
          const cells = Array.from(row.children) as HTMLTableCellElement[];
          for (const c of cells) {
            if (c === cell) break;
            columnIndex += c.colSpan || 1;
          }

          dispatch({
            type: "SET_HOVERED_CELL",
            payload: { rowIndex, columnIndex },
          });
          return;
        }
      }
    }
  }, []);

  useEffect(() => {
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tableElement = target.closest("table") as HTMLTableElement | null;

      if (tableElement) {
        showTableControls(tableElement);
        updateHoveredCell(event);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tableElement = target.closest("table") as HTMLTableElement | null;

      if (tableElement) {
        updateHoveredCell(event);
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target;
      const relatedTarget = event.relatedTarget;

      // If we're leaving a table and not going to another part of the table or the controls
      if (
        closestFromEventTarget(target, "table") &&
        !closestFromEventTarget(relatedTarget, "table") &&
        !closestFromEventTarget(relatedTarget, ".table-hover-actions-container")
      ) {
        hideTableControls();
      }
    };

    editorRoot.addEventListener("mouseover", handleMouseOver);
    editorRoot.addEventListener("mousemove", handleMouseMove);
    editorRoot.addEventListener("mouseout", handleMouseOut);

    return () => {
      editorRoot.removeEventListener("mouseover", handleMouseOver);
      editorRoot.removeEventListener("mousemove", handleMouseMove);
      editorRoot.removeEventListener("mouseout", handleMouseOut);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [editor, showTableControls, hideTableControls, updateHoveredCell]);

  // Update positions on table mutations
  useEffect(() => {
    return editor.registerMutationListener(TableNode, () => {
      const currentState = stateRef.current;
      if (
        currentState.tablePositions &&
        currentState.tablePositions.tableElement.isConnected
      ) {
        const positions = calculateButtonPositions(
          currentState.tablePositions.tableElement
        );
        dispatch({ type: "UPDATE_POSITIONS", payload: positions });
      }
    });
  }, [editor, calculateButtonPositions]);

  // Use ResizeObserver to update positions on table resize
  const tableElement = state.tablePositions?.tableElement;
  useEffect(() => {
    if (!tableElement || !state.isHovering) return;

    const resizeObserver = new ResizeObserver(() => {
      const currentState = stateRef.current;
      if (
        currentState.tablePositions &&
        currentState.tablePositions.tableElement.isConnected
      ) {
        const positions = calculateButtonPositions(
          currentState.tablePositions.tableElement
        );
        dispatch({ type: "UPDATE_POSITIONS", payload: positions });
      }
    });

    resizeObserver.observe(tableElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tableElement, state.isHovering, calculateButtonPositions]);

  // Memoize hover zone styles to prevent re-creation on every render
  const hoverZoneStyles = useMemo(() => {
    return state.tableHoverZones.map((zone) => ({
      topZone: {
        position: "fixed" as const,
        top: zone.topZone.top,
        left: zone.topZone.left,
        width: zone.topZone.width,
        height: zone.topZone.height,
      },
      leftZone: {
        position: "fixed" as const,
        top: zone.leftZone.top,
        left: zone.leftZone.left,
        width: zone.leftZone.width,
        height: zone.leftZone.height,
      },
    }));
  }, [state.tableHoverZones]);

  // Memoize extension styles
  const extensionStyles = useMemo(() => {
    if (!state.tablePositions) return null;
    return {
      top: {
        position: "fixed" as const,
        top: state.tablePositions.hoverExtensions.top.top,
        left: state.tablePositions.hoverExtensions.top.left,
        width: state.tablePositions.hoverExtensions.top.width,
        height: state.tablePositions.hoverExtensions.top.height,
      },
      left: {
        position: "fixed" as const,
        top: state.tablePositions.hoverExtensions.left.top,
        left: state.tablePositions.hoverExtensions.left.left,
        width: state.tablePositions.hoverExtensions.left.width,
        height: state.tablePositions.hoverExtensions.left.height,
      },
    };
  }, [state.tablePositions]);

  const handleMouseLeaveWithCheck = useCallback(
    (e: React.MouseEvent) => {
      const relatedTarget = e.relatedTarget;
      if (
        !closestFromEventTarget(relatedTarget, "table") &&
        !closestFromEventTarget(relatedTarget, ".table-hover-actions-container")
      ) {
        hideTableControls();
      }
    },
    [hideTableControls]
  );

  const handleMouseLeaveWithDragCheck = useCallback(
    (e: React.MouseEvent) => {
      if (stateRef.current.dragInfo) return; // Don't hide during drag
      handleMouseLeaveWithCheck(e);
    },
    [handleMouseLeaveWithCheck]
  );

  const tablePositions = state.tablePositions;

  return createPortal(
    <div className="table-hover-actions-container">
      {/* Always render invisible hover detection zones around all tables */}
      {state.tableHoverZones.map((zone, index) => (
        <div key={`zone-${index}`}>
          {/* Zone above the table */}
          <div
            className="table-hover-detection-zone"
            style={hoverZoneStyles[index].topZone}
            onMouseEnter={() => showTableControls(zone.tableElement)}
            onMouseLeave={handleMouseLeaveWithCheck}
          />
          {/* Zone to the left of the table */}
          <div
            className="table-hover-detection-zone"
            style={hoverZoneStyles[index].leftZone}
            onMouseEnter={() => showTableControls(zone.tableElement)}
            onMouseLeave={handleMouseLeaveWithCheck}
          />
        </div>
      ))}

      {/* Render buttons only when hovering */}
      {state.isHovering && tablePositions && extensionStyles && (
        <>
          {/* Invisible hover extension areas to keep controls visible when moving towards them */}
          <div
            className="table-hover-extension"
            style={extensionStyles.top}
            onMouseLeave={handleMouseLeaveWithCheck}
          />
          <div
            className="table-hover-extension"
            style={extensionStyles.left}
            onMouseLeave={handleMouseLeaveWithCheck}
          />

          {/* Sort Column Buttons - for each column */}
          {tablePositions.columnSortPositions.map((pos) => {
            const tableSort = sortState[tablePositions.tableNodeKey];
            const isActive = tableSort?.columnIndex === pos.index;
            const direction = isActive ? tableSort?.direction : null;
            const nextDirection =
              isActive && tableSort?.direction === "asc" ? "desc" : "asc";

            return (
              <button
                key={`sort-col-${pos.index}`}
                className={`table-sort-column-button${isActive ? " active" : ""}`}
                style={{
                  position: "fixed",
                  top: pos.top,
                  left: pos.left,
                }}
                onClick={() => handleSortColumn(pos.index)}
                onMouseLeave={handleMouseLeaveWithCheck}
                title={`Sort column ${pos.index + 1} ${nextDirection}`}
                aria-label={`Sort column ${pos.index + 1} ${nextDirection}`}
                type="button"
              >
                {direction === "asc" ? (
                  <ArrowUpward />
                ) : direction === "desc" ? (
                  <ArrowDownward />
                ) : (
                  <Sort />
                )}
              </button>
            );
          })}

          {/* Add Column Button - only for hovered column */}
          {state.hoveredColumnIndex !== null &&
            tablePositions.columnPositions
              .filter((pos) => pos.index === state.hoveredColumnIndex)
              .map((pos) => (
                <button
                  key={`col-${pos.index}`}
                  className="table-add-column-button"
                  style={{
                    position: "fixed",
                    top: pos.top,
                    left: pos.left,
                  }}
                  onClick={() => handleAddColumn(pos.index)}
                  onMouseLeave={handleMouseLeaveWithCheck}
                  title="Add column"
                  aria-label={`Add column after column ${pos.index + 1}`}
                  type="button"
                >
                  <Add />
                </button>
              ))}

          {/* Add Row Button - only for hovered row */}
          {state.hoveredRowIndex !== null &&
            tablePositions.rowPositions
              .filter((pos) => pos.index === state.hoveredRowIndex)
              .map((pos) => (
                <button
                  key={`row-${pos.index}`}
                  className="table-add-row-button"
                  style={{
                    position: "fixed",
                    top: pos.top,
                    left: pos.left,
                  }}
                  onClick={() => handleAddRow(pos.index)}
                  onMouseLeave={handleMouseLeaveWithCheck}
                  title="Add row"
                  aria-label={`Add row after row ${pos.index + 1}`}
                  type="button"
                >
                  <Add />
                </button>
              ))}

          {/* Reorder Column Button - only for hovered column */}
          {state.hoveredColumnIndex !== null &&
            tablePositions.columnReorderPositions
              .filter((pos) => pos.index === state.hoveredColumnIndex)
              .map((pos) => (
                <button
                  key={`reorder-col-${pos.index}`}
                  className={`table-reorder-column-button${
                    state.dragInfo?.type === "column" &&
                    state.dragInfo.sourceIndex === pos.index
                      ? " dragging"
                      : ""
                  }`}
                  style={{
                    position: "fixed",
                    top: pos.top,
                    left: pos.left,
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", `column-${pos.index}`);
                    handleDragStart("column", pos.index);
                  }}
                  onDragEnd={handleDragEnd}
                  onMouseLeave={handleMouseLeaveWithDragCheck}
                  title="Drag to reorder column"
                  aria-label={`Reorder column ${pos.index + 1}`}
                  type="button"
                >
                  <DragHandleOutlined />
                </button>
              ))}

          {/* Reorder Row Button - only for hovered row */}
          {state.hoveredRowIndex !== null &&
            tablePositions.rowReorderPositions
              .filter((pos) => pos.index === state.hoveredRowIndex)
              .map((pos) => (
                <button
                  key={`reorder-row-${pos.index}`}
                  className={`table-reorder-row-button${
                    state.dragInfo?.type === "row" &&
                    state.dragInfo.sourceIndex === pos.index
                      ? " dragging"
                      : ""
                  }`}
                  style={{
                    position: "fixed",
                    top: pos.top,
                    left: pos.left,
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", `row-${pos.index}`);
                    handleDragStart("row", pos.index);
                  }}
                  onDragEnd={handleDragEnd}
                  onMouseLeave={handleMouseLeaveWithDragCheck}
                  title="Drag to reorder row"
                  aria-label={`Reorder row ${pos.index + 1}`}
                  type="button"
                >
                  <DragHandleOutlined />
                </button>
              ))}

          {/* Invisible drop zones for row reordering - appear during drag */}
          {state.dragInfo?.type === "row" &&
            tablePositions.rowDropZones.map((zone, i) => (
              <div
                key={`row-drop-zone-${i}`}
                className="table-drop-zone table-drop-zone-row"
                style={{
                  position: "fixed",
                  top: zone.top,
                  left: zone.left,
                  width: zone.width,
                  height: zone.height,
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  handleDragOver(zone.index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(zone.index);
                }}
              />
            ))}

          {/* Invisible drop zones for column reordering - appear during drag */}
          {state.dragInfo?.type === "column" &&
            tablePositions.columnDropZones.map((zone, i) => (
              <div
                key={`col-drop-zone-${i}`}
                className="table-drop-zone table-drop-zone-column"
                style={{
                  position: "fixed",
                  top: zone.top,
                  left: zone.left,
                  width: zone.width,
                  height: zone.height,
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  handleDragOver(zone.index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(zone.index);
                }}
              />
            ))}

          {/* Drop indicator for column reordering - shows at the right edge of target column */}
          {state.dragInfo?.type === "column" &&
            state.dragInfo.currentTargetIndex !== null &&
            state.dragInfo.currentTargetIndex !== state.dragInfo.sourceIndex &&
            tablePositions.columnDropZones[
              state.dragInfo.currentTargetIndex
            ] && (
              <div
                className="table-drop-indicator table-drop-indicator-column"
                style={{
                  position: "fixed",
                  top: tablePositions.columnDropZones[
                    state.dragInfo.currentTargetIndex
                  ].top,
                  left: tablePositions.columnDropZones[
                    state.dragInfo.currentTargetIndex
                  ].indicatorPosition,
                  height:
                    tablePositions.columnDropZones[
                      state.dragInfo.currentTargetIndex
                    ].height,
                }}
              />
            )}

          {/* Drop indicator for row reordering - shows at the bottom edge of target row */}
          {state.dragInfo?.type === "row" &&
            state.dragInfo.currentTargetIndex !== null &&
            state.dragInfo.currentTargetIndex !== state.dragInfo.sourceIndex &&
            tablePositions.rowDropZones[state.dragInfo.currentTargetIndex] && (
              <div
                className="table-drop-indicator table-drop-indicator-row"
                style={{
                  position: "fixed",
                  top: tablePositions.rowDropZones[
                    state.dragInfo.currentTargetIndex
                  ].indicatorPosition,
                  left: tablePositions.rowDropZones[
                    state.dragInfo.currentTargetIndex
                  ].left,
                  width:
                    tablePositions.rowDropZones[
                      state.dragInfo.currentTargetIndex
                    ].width,
                }}
              />
            )}
        </>
      )}
    </div>,
    document.body
  );
}

export default TableHoverActionsPlugin;
